import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Tag } from 'lucide-react';
import type { KnowledgeBase } from '../../types/knowledge';
import type { Assistant } from '../../types/api';
import { Form, FormField, FormLabel, FormInput, FormTextarea, Button, Badge } from '../ui';

const knowledgeBaseSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome deve ter no máximo 100 caracteres'),
  description: z.string().optional(),
  category: z.string().max(100, 'Categoria deve ter no máximo 100 caracteres').optional(),
  content: z.string().min(1, 'Conteúdo é obrigatório'),
});

type FormData = z.infer<typeof knowledgeBaseSchema>;

interface KnowledgeBaseFormProps {
  entry: KnowledgeBase | null;
  assistants: Assistant[];
  selectedAssistants: number[];
  onAssistantsChange: (assistants: number[]) => void;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  onClose: () => void;
}

function KnowledgeBaseForm({
  entry,
  assistants,
  selectedAssistants,
  onAssistantsChange,
  tags,
  onTagsChange,
  onSubmit,
  isLoading,
  onClose,
}: KnowledgeBaseFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(knowledgeBaseSchema),
    defaultValues: {
      name: entry?.name || '',
      description: entry?.description || '',
      category: entry?.category || '',
      content: entry?.content || '',
    },
  });

  const [newTag, setNewTag] = useState('');

  const handleAddTag = () => {
    if (newTag && !tags.includes(newTag)) {
      onTagsChange([...tags, newTag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormField>
        <FormLabel htmlFor="name" required>
          Nome
        </FormLabel>
        <FormInput
          {...register('name')}
          error={!!errors.name}
          validationError={errors.name?.message}
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="description">Descrição</FormLabel>
        <FormTextarea
          {...register('description')}
          error={!!errors.description}
          validationError={errors.description?.message}
          rows={2}
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="category">Categoria</FormLabel>
        <FormInput
          {...register('category')}
          error={!!errors.category}
          validationError={errors.category?.message}
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="content" required>
          Conteúdo
        </FormLabel>
        <FormTextarea
          {...register('content')}
          error={!!errors.content}
          validationError={errors.content?.message}
          rows={10}
          className="font-mono text-sm"
        />
      </FormField>

      <FormField>
        <FormLabel>Tags</FormLabel>
        <div className="flex gap-2 mb-2">
          <FormInput
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            placeholder="Adicionar tag"
            onKeyPress={handleKeyPress}
          />
          <Button type="button" onClick={handleAddTag} disabled={!newTag}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {tags.map(tag => (
            <Badge key={tag} className="flex items-center gap-1" variant="secondary">
              <Tag className="h-3 w-3" />
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 hover:text-red-500"
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      </FormField>

      <FormField>
        <FormLabel>Assistentes</FormLabel>
        <div className="border rounded-md p-4 space-y-2 max-h-48 overflow-y-auto">
          {assistants?.map((assistant: Assistant) => (
            <div key={assistant.id} className="flex items-center">
              <input
                type="checkbox"
                id={`assistant-${assistant.id}`}
                checked={selectedAssistants.includes(assistant.id)}
                onChange={e => {
                  if (e.target.checked) {
                    onAssistantsChange([...selectedAssistants, assistant.id]);
                  } else {
                    onAssistantsChange(selectedAssistants.filter(id => id !== assistant.id));
                  }
                }}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label
                htmlFor={`assistant-${assistant.id}`}
                className="ml-2 block text-sm text-gray-900"
              >
                {assistant.name} ({assistant.type})
              </label>
            </div>
          ))}
        </div>
      </FormField>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {entry ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default KnowledgeBaseForm;
